/**
 * Where a call to action sends a reader, and what the offers on the site say.
 *
 * Several places offer an account and most of them draw their own markup. The
 * badge on a 词典 entry is minified with unquoted attributes, the segmenter
 * builds elements in the browser, and the rest write HTML strings, so what they
 * share is the address, the class and the words. A helper drawing the whole
 * line would have to serve all three shapes.
 *
 * The offers used to say "Sign in to save words" and link `/user/sign-in/`,
 * which put the sign-in form in front of somebody who had no account and left
 * them to find the Sign up link on it. `/user/sign-up/` lands on the form that
 * makes one. `src/user/authorize-request.ts` has the managed login side of
 * that.
 *
 * This module holds constants and imports nothing, which is what lets the
 * handler, both page renderers, the site bundle and the tools bundle all read
 * it. `nav-account.html` and `partials/liju-save.html` write the same words out
 * by hand, Hugo having no way to import a module, the way
 * `src/user/user-labels.ts` and the account menu already do.
 */

/** The route a call to action links, which starts a sign-up at managed login. */
export const signUpRoute = "/user/sign-up/";

/**
 * The class on a link to that route.
 *
 * It does two jobs, both of them somewhere else. `src/site/sign-in-link.ts`
 * rewrites the href to carry the page the reader was on, and `modules/nav`
 * hides the class from a reader whose session hint says they have an account.
 * `cb-sign-in` is the same pair of jobs for the bar's own link.
 */
export const signUpLinkClass = "cb-sign-up";

/**
 * What the offer under a save control says.
 *
 * One verb runs through the whole feature, so a word is saved here as it is on
 * the badge, in the list and in the name the list carries. "Free" is in the
 * copy because the price is the objection a reader has at the moment they meet
 * it.
 */
export const saveWordOffer = "Create a free account to save words";

/** The same offer under a 例句. */
export const saveLijuOffer = "Create a free account to save sentences";

/**
 * What the line near the top of a 词典 entry says.
 *
 * It says what a free account does on that page, which is to keep the word
 * and play its recordings. 复习 is Pro 专业, so the line leaves review out.
 */
export const entryStrapOffer =
  "Sign up for a free account to save words and hear them read aloud";

/** The same line near the top of a 例句. */
export const lijuStrapOffer =
  "Sign up for a free account to save sentences and hear them read aloud";

/**
 * What the offer under a 词典 or 例句 recording says.
 *
 * "Sign in" where the other offers say "Create", because a reader pressing a
 * play button has not gone looking for an account and the shorter verb asks
 * less of them. It still links the sign-up form like every other offer here,
 * and managed login puts the way to sign in on that form for a reader who has
 * an account already.
 */
export const listenOffer = "Sign in to listen to audio for free";
